import React, { useCallback, useEffect, useState } from 'react';

import { isEmpty } from 'lodash';

import ChatMessage from '~/components/Chat/ChatMessage';
import CliveChatMessage from '~/components/Chat/CliveChatMessage';
import { withAccountDocuments } from '~/components/Chat/context/ChatDocumentContext.tsx';
import { UserAvatar } from '~/components/Chat/UserAvatar';
import ChatInput from '~/components/ChatInput/ChatInput';
import CliveClaimSuccessMessage from '~/components/Fnol/CliveClaimSuccessMessage';
import CliveCreateClaimMessage from '~/components/Fnol/CliveCreateClaimMessage';
import CliveErrorMessage from '~/components/Fnol/CliveErrorMessage';
import { FnolHeader } from '~/components/Fnol/FnolHeader';
import {
  FnolCreateClaimResponse,
  FnolCreationResponse,
  FnolPrettyResponse,
  FnolTaskStatusResponse,
} from '~/components/Fnol/types';
import { UploadedFilesPanel } from '~/components/Fnol/UploadedFilesPanel';
import { useAuth } from '~/context/AuthContext';
import { useMainTopBar } from '~/context/MainTopBarContext';
import { useRealTimeUpdatesTask } from '~/context/RealTimeUpdatesContext';
import { MarkdownSection } from '~/core/atoms/MarkdownSection';
import { ResizeableHandle, ResizeablePanel, ResizeablePanels } from '~/core/atoms/ResizeablePanels';
import ClipIcon from '~/core/iconComponents/icons/ClipIcon';
import FileUploadModal, { UploadedFile } from '~/core/molecules/FileUpload/FileUploadModal';
import SkeletonTable from '~/core/molecules/SkeletonTable';
import cn from '~/utils/cn';
import { parseDateWithOptionalTimezone } from '~/utils/Dates/DateTimeUtils';
import { useInterval } from '~/utils/hooks/useInterval';
import { get, post } from '~/utils/request';

const FNOL_POLLING_TIMEOUT = 5000;

const Fnol: React.FC = () => {
  const [value, setValue] = useState('');
  const [postedText, setPostedText] = useState('');
  const [postedTextOrFiles, setPostedTextOrFiles] = useState(false);
  const [fnol, setFnol] = useState<object | null>(null);
  const [prettyFnol, setPrettyFnol] = useState<string | null>(null);
  const [fnolTaskId, setFnolTaskId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingClaim, setIsCreatingClaim] = useState(false);
  const [isClaimCreated, setIsClaimCreated] = useState(false);
  const [claimNumber, setClaimNumber] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fnolRequestFailed, setFnolRequestFailed] = useState<boolean>(false);
  const [claimRequestFailed, setClaimRequestFailed] = useState<boolean>(false);
  const [openFileUpload, setOpenFileUpload] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const { user } = useAuth();

  const { resetMainTopBar } = useMainTopBar();
  const { connect, disconnect, updates } = useRealTimeUpdatesTask();

  const showSidePanel = !!fnolTaskId || isLoading;

  // Reset main top bar on mount
  useEffect(resetMainTopBar, [resetMainTopBar]);

  // Connect to real-time updates when task is created
  useEffect(() => {
    if (!fnolTaskId) return;

    connect(fnolTaskId);
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fnolTaskId]);

  // Log real-time updates to console (future: display in Timeline)
  useEffect(() => {
    if (updates.length === 0) return;

    const latestUpdate = updates[updates.length - 1];
    console.log('[FNOL] Real-time update:', latestUpdate);
  }, [updates]);

  const handleSubmit = useCallback(
    async (text: string) => {
      const trimmedText = text.trim();
      const hasUploadedFiles = !isEmpty(uploadedFiles);
      if (!trimmedText && !hasUploadedFiles) {
        return;
      }

      setIsLoading(true);
      setPostedText(trimmedText);
      setPostedTextOrFiles(true);
      setValue('');
      try {
        const response = await post<FnolCreationResponse>({
          url: '/api/v1/fnol',
          data: { text: trimmedText, files: uploadedFiles },
        });
        setFnolTaskId(response.task_id);
      } catch {
        setIsLoading(false);
        setError('Sorry, could not send the FNOL request. Please try again.');
        setFnolRequestFailed(true);
      }
    },
    [uploadedFiles]
  );

  const onSubmitFiles = useCallback(async (files: UploadedFile[]) => {
    setUploadedFiles((prev) => [...prev, ...files]);
  }, []);

  const onRemoveUploadedFile = useCallback((file: UploadedFile) => {
    setUploadedFiles((prev) => prev.filter((f) => f.name !== file.name));
  }, []);

  const getPrettyFnol = useCallback(async () => {
    if (!fnolTaskId) return;
    try {
      const fnolPrettyResponse = await get<FnolPrettyResponse>({
        url: `/api/v1/fnol/${fnolTaskId}/pretty_fnol`,
        timeout: 3 * 60 * 1000, // 3 minutes,
      });
      setPrettyFnol(fnolPrettyResponse.pretty_fnol ?? null);
    } catch {
      setError('Sorry, could not get the FNOL data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [fnolTaskId]);

  const checkFnolReady = useCallback(async () => {
    if (!fnolTaskId) return;
    try {
      setIsLoading(true);
      const response = await get<FnolTaskStatusResponse>({ url: `/api/v1/fnol/${fnolTaskId}` });
      if (!response.is_completed) return;

      setFnol(response.fnol as object);
      await getPrettyFnol();
      setIsLoading(false);
    } catch {
      setError('Failed to fetch FNOL status. Please try again.');
      setFnolRequestFailed(true);
      setIsLoading(false);
    }
  }, [fnolTaskId, getPrettyFnol]);

  const { start: startPolling, stop: stopPolling } = useInterval(checkFnolReady, FNOL_POLLING_TIMEOUT);

  const handleCreateClaim = useCallback(async () => {
    if (!fnol || !fnolTaskId) return;
    setError(null);
    setIsClaimCreated(false);
    setClaimNumber(null);
    setIsCreatingClaim(true);
    setClaimRequestFailed(false);
    try {
      const { extracted_data } = fnol as { extracted_data: object };
      const response = await post<FnolCreateClaimResponse>({
        url: `/api/v1/claims/${fnolTaskId}/create_claim`,
        data: {
          fnol_extracted_data: extracted_data,
        },
      });
      setIsClaimCreated(true);
      setClaimNumber(response?.claim_number as string);
    } catch {
      setIsLoading(false);
      setError('Failed to create claim. Please try again.');
      setClaimRequestFailed(true);
    } finally {
      setIsCreatingClaim(false);
    }
  }, [fnol, fnolTaskId]);

  const handleRetryFnolRequest = useCallback(async () => {
    setValue(postedText);
    setError(null);
    setFnolRequestFailed(false);
    setFnolTaskId(null);
    setFnol(null);
    setPrettyFnol(null);
    setIsCreatingClaim(false);
    setIsClaimCreated(false);
    setClaimNumber(null);

    await handleSubmit(postedText);
  }, [postedText, setValue, handleSubmit]);

  useEffect(() => {
    if (!fnolTaskId || fnol || prettyFnol || fnolRequestFailed) {
      stopPolling();
      return;
    }

    startPolling();
  }, [fnolTaskId, fnol, startPolling, stopPolling, prettyFnol, fnolRequestFailed]);

  return (
    <div className="__ROOT__ bg-gradient-3 flex w-full min-w-4xl flex-1 overflow-auto bg-cover">
      <ResizeablePanels className="__MAIN_CONTAINER__ flex h-full">
        <ResizeablePanel defaultSize={showSidePanel ? 60 : 100} minSize={40}>
          <div className="__PANEL_1__ flex h-full w-full items-center justify-center overflow-auto">
            <div
              className={cn('flex h-full w-10/12 max-w-3xl flex-col items-end justify-start gap-20 rounded-md py-40', {
                'justify-center': !postedTextOrFiles,
              })}
            >
              {!postedTextOrFiles && <FnolHeader />}

              {postedTextOrFiles && postedText && (
                <ChatMessage
                  message={postedText}
                  sender="You"
                  className="bg-bluesky-700/5 rounded-lg"
                  createdAt={parseDateWithOptionalTimezone(new Date().toISOString())}
                  avatar={<UserAvatar user={user} />}
                  direction="rtl"
                />
              )}

              {isLoading && !fnol && <CliveChatMessage isLoading message="" />}
              {fnolRequestFailed && <CliveErrorMessage onRetry={handleRetryFnolRequest} />}

              {fnol && (
                <>
                  <CliveCreateClaimMessage
                    onCreateClaim={handleCreateClaim}
                    isLoading={isCreatingClaim}
                    disabled={isCreatingClaim || isClaimCreated}
                    isClaimCreated={isClaimCreated}
                    showRetry={claimRequestFailed}
                  />
                  {isClaimCreated && claimNumber && <CliveClaimSuccessMessage claimNumber={claimNumber} />}
                </>
              )}

              {postedTextOrFiles ? <div className="__FNOL_CHAT_SPACER__ min-h-80 w-full" /> : null}

              <div
                className={cn(
                  '__CHAT_INPUT_CONTAINER__ w-full',
                  `${postedTextOrFiles ? 'sticky bottom-20 mt-auto' : ''}`
                )}
              >
                {isEmpty(uploadedFiles) ? null : (
                  <UploadedFilesPanel
                    uploadedFiles={uploadedFiles}
                    onRemove={onRemoveUploadedFile}
                    className="mx-80 -mb-4 rounded-t-lg p-16 pb-12"
                    disabled={isLoading || postedTextOrFiles}
                  />
                )}

                <ChatInput
                  value={value}
                  rows={1}
                  disabled={postedTextOrFiles || isLoading}
                  onTextChange={(e) => setValue(e.target.value)}
                  onSubmit={handleSubmit}
                  className={cn('__CHAT_INPUT__ w-full')}
                  placeholder="Type or paste here the incident description"
                  actions={[
                    {
                      key: 'clip',
                      icon: <ClipIcon className="text-slate-700" />,
                      onClick: () => setOpenFileUpload(true),
                      ariaLabel: 'Attach files',
                      disabled: isLoading || postedTextOrFiles,
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </ResizeablePanel>
        {showSidePanel ? (
          <>
            <ResizeableHandle className="ml-20 w-4 bg-transparent hover:bg-transparent" />
            <ResizeablePanel defaultSize={40} minSize={20}>
              <div className="__PANEL_2__ h-full w-full overflow-auto bg-white p-12 text-wrap">
                {isLoading ? (
                  <div className="__SKELETON_TABLE__ flex flex-col items-center gap-12">
                    <SkeletonTable rowsCount={3} columnsCount={2} isError={!!error} />
                    <SkeletonTable rowsCount={3} columnsCount={2} />
                    <span className="text-slate-800">
                      {error ? error : fnol ? 'Fnol created, generating summery...' : 'Please wait...'}
                    </span>
                    <SkeletonTable rowsCount={3} columnsCount={2} />
                  </div>
                ) : (
                  <div className="__MARKDOWN_SECTION__ flex h-full flex-col">
                    <h2 className="my-8 px-12 text-xl font-semibold">FNOL Form Preview</h2>
                    <h4 className="mb-20 border-0 border-b-1 px-12 pb-16 text-sm text-slate-800">
                      First Notice of Loss (FNOL) Form
                    </h4>
                    <MarkdownSection markdown={prettyFnol ?? ''} className="pb-40" />
                  </div>
                )}
              </div>
            </ResizeablePanel>
          </>
        ) : null}
      </ResizeablePanels>
      {openFileUpload ? (
        <FileUploadModal
          isOpen
          uploadUrl="/api/v1/fnol/files"
          onClose={() => setOpenFileUpload(false)}
          onSubmit={onSubmitFiles}
        />
      ) : null}
    </div>
  );
};

export default withAccountDocuments(Fnol);
